/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import './feature-wiring.js';

import { EcsperClient, type TokenProvider } from '@ecsper/api-client';
import { EcsperAuthService } from '@ecsper/auth';
import { LEDGER_EVENTS } from '@ecsper/forensic-ledger';
import type { SpecialityModule } from '@ecsper/speciality-contract';
import { ExpertWitnessModule } from '@ecsper/speciality-expert-witness';
import { noopTelemetryService, type TelemetryService } from '@ecsper/telemetry';
import type { OfficePlatform } from '@ecsper/types';
import { VaultClient } from '@ecsper/vault-client';

import { assertOfficeSsoBuildConfig } from './office-sso-build-config.js';

/** Resolve API origin (no trailing slash). Runtime: `import.meta.env.VITE_API_URL`, Stage 5.5 fallback when unset. */
export function resolveApiBaseUrl(): string {
  const raw = import.meta.env.VITE_API_URL?.trim();
  // Stage 5.5 dev API host per Standard 38 §4.2/§4.9 (Drift D2 tactical close)
  const base = raw !== undefined && raw !== '' ? raw : 'https://dev.api.ecsper.io';
  return base.replace(/\/$/, '');
}

export function resolveWorkspaceBaseUrl(): string {
  return `${resolveApiBaseUrl()}/api/v1/workspace`;
}

function parseOfficePlatform(raw: string | undefined): OfficePlatform {
  const v = raw ?? '';
  const allowed: readonly OfficePlatform[] = ['Win32', 'Mac', 'OfficeOnline', 'iOS', 'Android'];
  return (allowed as readonly string[]).includes(v) ? (v as OfficePlatform) : 'OfficeOnline';
}

export interface AppContext {
  readonly auth: EcsperAuthService;
  /** Typed HTTP client for Workspace and Platform paths (same origin as {@link resolveApiBaseUrl}). */
  readonly apiClient: EcsperClient;
  readonly vaultClient: VaultClient;
  readonly specialities: readonly SpecialityModule[];
  /** Active speciality for Stage 5.5 (single-speciality demo). */
  readonly activeSpeciality: SpecialityModule;
  /** Office host info (Word desktop / Word online / etc.). */
  readonly hostInfo: {
    readonly host: 'Word';
    readonly platform: string;
    readonly version: string;
  };
  /** Disabled telemetry implementation until Stage 6 wiring. */
  readonly telemetry: TelemetryService;
  /** Forensic ledger event name map (composition anchor for @ecsper/forensic-ledger). */
  readonly forensicLedgerEventNames: typeof LEDGER_EVENTS;
}

let autoShowEnabled = false;

/**
 * Enable task pane auto-show for future sessions and show the pane now.
 *
 * - setStartupBehavior(load): loads the runtime on next document open
 * - showAsTaskpane(): makes the task pane visible immediately
 *
 * Both require SharedRuntime 1.1. Feature detection ensures graceful
 * degradation on hosts that do not support the APIs.
 */
export async function enableTaskPaneAutoShow(): Promise<void> {
  if (autoShowEnabled) {
    return;
  }
  autoShowEnabled = true;

  try {
    if (Office.addin?.setStartupBehavior) {
      await Office.addin.setStartupBehavior(Office.StartupBehavior.load);
      // eslint-disable-next-line no-console -- operator confirmation for manual test plan
      console.info('[Ecsper] Startup behaviour set to load for future sessions');
    }
  } catch (e) {
    // eslint-disable-next-line no-console -- hosts without SharedRuntime 1.1 fail silently
    console.info(
      '[Ecsper] setStartupBehavior not available:',
      e instanceof Error ? e.message : String(e),
    );
  }

  try {
    if (Office.addin?.showAsTaskpane) {
      await Office.addin.showAsTaskpane();
      // eslint-disable-next-line no-console -- operator confirmation for manual test plan
      console.info('[Ecsper] Task pane shown via showAsTaskpane()');
    }
  } catch (e) {
    // eslint-disable-next-line no-console -- pane may already be visible or API unsupported
    console.info(
      '[Ecsper] showAsTaskpane not available:',
      e instanceof Error ? e.message : String(e),
    );
  }
}

/** Resets auto-show idempotency guard (unit tests only). */
export function resetTaskPaneAutoShowStateForTests(): void {
  autoShowEnabled = false;
}

/**
 * Initialises auth, API client, vault client, and the speciality registry.
 * Called once after Office.onReady. Runs {@link EcsperAuthService.authenticate} so SSO
 * exchange completes before the vault client is used (may leave `office_picker` state).
 */
export async function bootstrap(): Promise<AppContext> {
  assertOfficeSsoBuildConfig();

  const hostInfo = {
    host: 'Word' as const,
    platform: String(Office.context.diagnostics?.platform ?? 'unknown'),
    version: String(Office.context.diagnostics?.version ?? 'unknown'),
  };

  const authHolder: { provideToken?: TokenProvider } = {};

  const apiClient = new EcsperClient({
    baseUrl: resolveApiBaseUrl(),
    tokenProvider: async () => {
      const provide = authHolder.provideToken;
      if (provide === undefined) {
        return null;
      }
      return provide();
    },
    clientId: 'ecsper-office-addin',
    clientVersion: '0.1.0',
  });

  const auth = new EcsperAuthService(apiClient, {
    host: hostInfo.host,
    hostVersion: hostInfo.version,
    platform: parseOfficePlatform(String(Office.context.platform)),
  });
  authHolder.provideToken = auth.getTokenProvider();

  auth.subscribe((state) => {
    if (state.status === 'authenticated') {
      void enableTaskPaneAutoShow();
    }
  });

  await auth.authenticate();

  const vaultClient = new VaultClient({
    baseUrl: resolveWorkspaceBaseUrl(),
    getToken: async () => {
      const token = await auth.getTokenProvider()();
      if (token === null || token === '') {
        throw new Error('Vault operations require an authenticated session.');
      }
      return token;
    },
  });

  const specialities: readonly SpecialityModule[] = [ExpertWitnessModule];
  const activeSpeciality = ExpertWitnessModule;

  return {
    auth,
    apiClient,
    vaultClient,
    specialities,
    activeSpeciality,
    hostInfo,
    telemetry: noopTelemetryService,
    forensicLedgerEventNames: LEDGER_EVENTS,
  };
}
